import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { blogService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const BlogDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { language } = useLanguage();

    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchPost();
    }, [slug, language]);

    const fetchPost = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await blogService.getPostBySlug(slug);
            setPost(data);
        } catch (err) {
            console.error('Blog detail error:', err);
            setError(language === 'fa' ? 'خطا در دریافت مقاله' : 'Error fetching post');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('fa-IR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getTranslation = (obj, field) => {
        if (!obj) return '';
        if (obj[language]?.[field]) return obj[language][field];
        const codes = Object.keys(obj);
        if (codes.length > 0 && obj[codes[0]]?.[field]) return obj[codes[0]][field];
        return '';
    };

    // Loading
    if (loading) {
        return (
            <div className="max-w-[1010px] mx-auto px-4 py-8 font-iransansRegular">
                <div className="flex justify-center items-center py-20">
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 border-4 border-[#1F4284] border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-[#1F4284] font-bold text-lg">
                            {language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Error
    if (error) {
        return (
            <div className="max-w-[1010px] mx-auto px-4 py-8 font-iransansRegular">
                <div className="flex justify-center items-center py-20">
                    <div className="flex flex-col items-center gap-4 bg-red-50 rounded-xl p-6">
                        <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <p className="text-red-600 font-bold text-lg">{error}</p>
                        <div className="flex gap-3">
                            <button onClick={fetchPost} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">
                                {language === 'fa' ? 'تلاش مجدد' : 'Try Again'}
                            </button>
                            <button onClick={() => navigate('/blogs')} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
                                {language === 'fa' ? 'بازگشت' : 'Back'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const title = post?.title || getTranslation(post?.translations, 'title');
    const content = post?.content || getTranslation(post?.translations, 'content');
    const keywords = post?.keywords || [];

    return (
        <div className="max-w-[1010px] mx-auto px-3 sm:px-4 py-4 lg:py-8 font-iransansRegular">
            {/* Back Button */}
            <motion.button
                onClick={() => navigate('/blogs')}
                className="flex items-center gap-2 text-[#204286] font-bold text-sm mb-6 hover:text-[#1F4284] transition-colors"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
            >
                <svg className="w-5 h-5 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {language === 'fa' ? 'بازگشت به مقالات' : 'Back to Blogs'}
            </motion.button>

            {/* Banner */}
            {post?.banner && (
                <motion.div
                    className="w-full h-48 sm:h-64 lg:h-[400px] rounded-2xl overflow-hidden mb-8"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <img src={post.banner} alt={title} className="w-full h-full object-cover" />
                </motion.div>
            )}

            {/* Başlık */}
            <motion.h1
                className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-[#003373] mb-4 leading-tight text-right"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
            >
                {title}
            </motion.h1>

            {/* Tarih ve Keywords */}
            <motion.div
                className="flex flex-wrap items-center justify-end gap-3 mb-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
            >
                <span className="text-sm text-gray-500">
                    {formatDate(post?.created_at)}
                </span>
                {keywords.length > 0 && (
                    <>
                        <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                        <div className="flex flex-wrap gap-2">
                            {keywords.map((keyword) => (
                                <span
                                    key={keyword.slug}
                                    className="px-3 py-1 bg-[#EFEFFF] text-[#003373] text-xs font-medium rounded-full"
                                >
                                    {keyword.word || getTranslation(keyword.translations, 'word')}
                                </span>
                            ))}
                        </div>
                    </>
                )}
            </motion.div>

            {/* İçerik */}
            <motion.div
                className="max-w-none text-right"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
            >
                <div
                    className="blog-content text-[#002450] text-sm sm:text-base lg:text-lg leading-loose font-medium"
                    dangerouslySetInnerHTML={{ __html: content }}
                />
            </motion.div>

            {/* Alt navigasyon */}
            <motion.div
                className="flex justify-between items-center mt-12 pt-8 border-t border-gray-200"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
            >
                <button
                    onClick={() => navigate('/blogs')}
                    className="flex items-center gap-2 text-[#FFB947] font-bold text-sm hover:text-[#e5a63a] transition-colors"
                >
                    <svg className="w-5 h-5 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {language === 'fa' ? 'همه مقالات' : 'All Posts'}
                </button>

                <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-400">
                        {language === 'fa' ? 'اشتراک‌گذاری' : 'Share'}
                    </span>
                    <button className="w-8 h-8 rounded-full bg-[#EFEFFF] flex items-center justify-center hover:bg-[#003373] hover:text-white transition-all">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                        </svg>
                    </button>
                    <button className="w-8 h-8 rounded-full bg-[#EFEFFF] flex items-center justify-center hover:bg-[#003373] hover:text-white transition-all">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                        </svg>
                    </button>
                </div>
            </motion.div>

            {/* Blog Content CSS - Kapsamlı */}
            <style>{`
    /* ========== CKEditor İçerik Stilleri ========== */
    .blog-content {
        color: #002450;
        line-height: 1.7;
    }
    
    /* ========== BAŞLIKLAR ========== */
    .blog-content h1 {
        color: #003373;
        font-size: 1.75rem;
    }
    .blog-content h2 {
        color: #003373;
        font-size: 1.4rem;
        margin: 1rem 0 0.75rem 0;
    }
    .blog-content h3 {
        color: #003373;
        font-size: 1.2rem;
        margin: 0.75rem 0 0.5rem 0;
    }
    .blog-content h4 {
        color: #003373;
        font-size: 1.1rem;
        margin: 0.5rem 0 0.5rem 0;
    }
    .blog-content h5 {
        color: #003373;
        font-size: 1rem;
        margin: 0.5rem 0 0.5rem 0;
    }
    .blog-content h6 {
        color: #003373;
        font-size: 0.9rem;
        margin: 0.5rem 0 0.5rem 0;
    }
    
    /* ========== PARAGRAF ========== */
    .blog-content p {
        margin: 0.75rem 0;
        line-height: 1.7;
        color: #002450;
        text-align: justify;
    }
    
    /* ========== LİSTELER ========== */
    .blog-content ul {
        list-style-type: disc !important;
        margin: 0.75rem 0 !important;
        padding-right: 2rem !important;
        color: #002450 !important;
    }
    .blog-content ol {
        list-style-type: decimal !important;
        margin: 0.75rem 0 !important;
        padding-right: 2rem !important;
        color: #002450 !important;
    }
    .blog-content ul ul {
        list-style-type: circle !important;
        margin: 0.5rem 0 !important;
        padding-right: 1.5rem !important;
    }
    .blog-content ul ul ul {
        list-style-type: square !important;
    }
    .blog-content ol ol {
        list-style-type: lower-alpha !important;
        margin: 0.5rem 0 !important;
        padding-right: 1.5rem !important;
    }
    .blog-content li {
        margin: 0.25rem 0 !important;
        line-height: 1.6 !important;
        color: #002450 !important;
        display: list-item !important;
        text-align: right !important;
    }
    
    /* ========== ALINTILAR ========== */
    .blog-content blockquote {
        border-right: 4px solid #FFB947;
        padding: 0.75rem 1rem;
        margin: 1rem 0;
        color: #003373;
        font-style: italic;
        background: #FFF8E1;
        border-radius: 0 8px 8px 0;
    }
    
    /* ========== RESİMLER (CKEditor 5) - RTL DÜZELTİLDİ ========== */
    .blog-content figure.image {
        display: table;
        margin: 0.5rem 0;
        max-width: 100%;
    }
    .blog-content figure.image img {
        max-width: 100%;
        height: auto;
        border-radius: 12px;
    }
    .blog-content figcaption {
        text-align: center;
        font-size: 0.875rem;
        color: #666;
        margin-top: 0.5rem;
    }
    
    /* Sağa hizalı (RTL'de resim sağda, metin solda) */
    .blog-content figure.image.image-style-align-right,
    .blog-content figure.image.image_resized.image-style-align-right {
        float: right !important;
        margin-left: 1.5rem !important;
        margin-right: 0 !important;
        margin-bottom: 1rem !important;
        max-width: 50% !important;
    }
    
    /* Sola hizalı (RTL'de resim solda, metin sağda) */
    .blog-content figure.image.image-style-align-left,
    .blog-content figure.image.image_resized.image-style-align-left {
        float: left !important;
        margin-right: 1.5rem !important;
        margin-left: 0 !important;
        margin-bottom: 1rem !important;
        max-width: 50% !important;
    }
    
    /* Ortalanmış resim */
    .blog-content figure.image.image-style-align-center,
    .blog-content figure.image.image_resized.image-style-align-center {
        display: block !important;
        margin-left: auto !important;
        margin-right: auto !important;
        float: none !important;
        clear: both !important;
        text-align: center !important;
        max-width: 100% !important;
    }
    
    /* Yan resim (RTL'de sağda) */
    .blog-content figure.image.image-style-side {
        float: right !important;
        margin-left: 1.5rem !important;
        margin-right: 0 !important;
        margin-bottom: 1rem !important;
        width: 35% !important;
    }
    
    /* Metin resim etrafında dolaşsın */
    .blog-content p,
    .blog-content ul,
    .blog-content ol,
    .blog-content blockquote {
        overflow: hidden;
    }
    
    /* Clearfix */
    .blog-content::after {
        content: "";
        display: table;
        clear: both;
    }
    
    /* ========== TABLOLAR ========== */
    .blog-content table {
        width: 100%;
        border-collapse: collapse;
        margin: 1rem 0;
        background: white;
        border-radius: 8px;
        overflow: hidden;
    }
    .blog-content table td,
    .blog-content table th {
        border: 1px solid #e2e8f0;
        padding: 0.75rem 1rem;
        text-align: right;
    }
    .blog-content table th {
        background: #EFEFFF;
        color: #003373;
        font-weight: 700;
    }
    .blog-content table td {
        color: #002450;
    }
    .blog-content tbody tr:nth-child(even) {
        background-color: #f8fafc;
    }
    .blog-content tbody tr:hover {
        background-color: #f1f5f9;
    }
    
    /* ========== LİNKLER ========== */
    .blog-content a {
        color: #1F4284;
        text-decoration: none;
        transition: color 0.3s;
    }
    .blog-content a:hover {
        color: #FFB947;
        text-decoration: underline;
    }
    
    /* ========== YATAY ÇİZGİ ========== */
    .blog-content hr {
        display: block;
        height: 1px;
        border: 0;
        background: #EBEBEB;
        margin: 1.5rem 0;
    }
    
    /* ========== KOD ========== */
    .blog-content pre {
        background: #1e293b;
        padding: 1rem;
        border-radius: 8px;
        overflow-x: auto;
        font-family: monospace;
        font-size: 0.85rem;
        color: #e2e8f0;
        margin: 1rem 0;
        direction: ltr;
        text-align: left;
    }
    .blog-content code {
        background: #EFEFFF;
        padding: 0.2rem 0.4rem;
        border-radius: 4px;
        font-family: monospace;
        font-size: 0.85rem;
        color: #003373;
        direction: ltr;
    }
    .blog-content pre code {
        background: transparent;
        color: inherit;
        padding: 0;
    }
    
    /* ========== METİN STİLLERİ ========== */
    .blog-content strong, .blog-content b {
        color: #003373;
        font-weight: 800;
    }
    .blog-content mark {
        background-color: #FFF8E1;
        color: #003373;
        padding: 0.1rem 0.3rem;
    }
    .blog-content small {
        font-size: 0.85em;
        color: #666;
    }
    
    /* ========== MEDYA ========== */
    .blog-content iframe,
    .blog-content video {
        max-width: 100%;
        border-radius: 8px;
        margin: 1rem auto;
        display: block;
    }
    
    /* ========== SPAN ve DİĞER INLINE ========== */
    .blog-content span {
        color: inherit;
    }
    
    /* ========== RESPONSIVE ========== */
    @media (max-width: 640px) {
        .blog-content h1 { font-size: 1.4rem !important; }
        .blog-content h2 { font-size: 1.2rem !important; }
        .blog-content h3 { font-size: 1.1rem !important; }
        .blog-content h4 { font-size: 1rem !important; }
        
        .blog-content figure.image.image-style-align-right,
        .blog-content figure.image.image-style-align-left,
        .blog-content figure.image.image-style-side {
            float: none !important;
            display: block !important;
            margin: 1rem auto !important;
            max-width: 100% !important;
            width: 100% !important;
        }
        
        .blog-content table {
            font-size: 0.8rem;
            display: block;
            overflow-x: auto;
        }
        .blog-content table th,
        .blog-content table td {
            padding: 0.5rem;
        }
    }
`}</style>
        </div>
    );
};

export default BlogDetail;